import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import useDialog from "../../../utils/hooks/useDialog";

// A dialog anchored to the right edge instead of the viewport centre. Only the
// geometry is new — focus trap, Escape, the refcounted body-scroll lock and
// focus restore all come from `useDialog`, the same hook the six centred modals
// already share, so a drawer stacked over one of them unlocks the page exactly
// once.
//
// The panel is a column with a scrolling BODY rather than a scrolling panel:
// a dialog that grows past the viewport inside a fixed shell is clipped at both
// ends with nothing to scroll, which is how the timetable lesson form lost its
// Save button on a laptop.
//
// Below `sm` it takes the full width: a 24rem sheet on a 390px screen leaves a
// strip of backdrop too thin to aim at, so the only way out would be Escape —
// which a phone does not have.
export default function SideDrawer({
  title,
  subtitle,
  onClose,
  children,
  footer = null,
  widthClass = "sm:max-w-xl",
}) {
  const { t } = useTranslation();
  const dialogRef = useDialog(onClose);
  const titleId = useId();
  // Mounting at the final position would show no motion at all, so the entry
  // transform is flipped one frame later. `motion-reduce` drops the travel for
  // anyone who asked the OS for less movement.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" onClick={onClose}>
      <div
        className={`absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity duration-200 motion-reduce:transition-none ${
          entered ? "opacity-100" : "opacity-0"
        }`}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className={`relative flex h-full w-full ${widthClass} flex-col bg-white shadow-2xl outline-none transition-transform duration-200 ease-out motion-reduce:transition-none sm:rounded-l-[32px] ${
          entered ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex shrink-0 items-start gap-4 border-b border-slate-100 bg-slate-50/60 px-6 py-5 sm:rounded-tl-[32px]">
          <div className="min-w-0 flex-1">
            <h3 id={titleId} className="truncate text-lg font-black text-slate-900">
              {title}
            </h3>
            {subtitle && (
              <p className="mt-0.5 text-xs font-medium text-slate-500">{subtitle}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-200 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>

        {footer && (
          <div className="shrink-0 border-t border-slate-100 bg-white px-6 py-4 sm:rounded-bl-[32px]">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
